import React, { useMemo } from 'react';

import type { ExperimentCohortVariationControl } from '@atlassian/touchpoint-commons-event-analytics/src/common/types';

import { useEditionAwareness } from '../../../services/edition-awareness-context';
import { useGetExperimentValue } from '../../../services/experiment-context';
import { getJiraApplicationKey } from '../../../services/get-jira-application-key';
import { getJiraProjectTypeKey } from '../../../services/get-jira-project-type-key';
import { getHamsKey } from '../../../services/product-to-hams-key';
import type { ExperimentCohort as ExperimentCohortTreatmentControl, Product } from '../../../types';
import { ModalEntryPoint } from '../shared/modal-entry-point';

import { DIALOG_CONFIG } from './dialog-config';
import { entryPoint, type EntryPointParams } from './entrypoint';
import { useNBEAExperiment } from './use-nbea-experiment';

function shouldIncludeGUPS(
	product: Product,
	nbeaExperimentCohort: ExperimentCohortTreatmentControl,
	cyeaExperimentCohort: ExperimentCohortVariationControl,
) {
	if (product !== 'confluence') {
		return false;
	}

	return (
		nbeaExperimentCohort === 'control' ||
		nbeaExperimentCohort === 'treatment' ||
		cyeaExperimentCohort === 'control' ||
		cyeaExperimentCohort === 'variation'
	);
}

export function FreeToStandardModal(): React.JSX.Element {
	const { product, cloudId } = useEditionAwareness();

	const jiraApplicationKey = getJiraApplicationKey(product);
	const projectType = getJiraProjectTypeKey(product);
	const { getExperimentValue } = useGetExperimentValue();

	const { abCohort: nbeaExperimentCohort } = useNBEAExperiment({
		fireExperimentExposure: false,
		product,
	});

	const cyeaExperimentCohort = getExperimentValue({
		statsigParams: {
			experimentName: 'confluence_free_choose_your_edition_experiment',
			parameterName: 'cohort',
			defaultValue: 'not-enrolled',
			options: {
				fireExperimentExposure: false,
			},
		},
	});

	const entryPointParams: EntryPointParams = useMemo(
		() => ({
			cloudId,
			hamsProductKey: getHamsKey(product),
			// Fallback to any valid value since this means that we're not using jira
			jiraApplicationKey: jiraApplicationKey ?? 'JIRA_SOFTWARE',
			// Fallback to any valid value since this means that we're not using jira
			projectType: projectType ?? 'SOFTWARE',
			isJira:
				product === 'jira' ||
				product === 'jira-product-discovery' ||
				product === 'jira-service-management',
			isConfluence: product === 'confluence',
			shouldIncludeGUPS: shouldIncludeGUPS(product, nbeaExperimentCohort, cyeaExperimentCohort),
		}),
		[nbeaExperimentCohort, cyeaExperimentCohort, cloudId, product, projectType, jiraApplicationKey],
	);

	const modalProps = useMemo(() => ({ width: 'x-large' }), []);

	return (
		<ModalEntryPoint
			entryPoint={entryPoint}
			entryPointParams={entryPointParams}
			modalProps={modalProps}
			dialogConfig={DIALOG_CONFIG}
		/>
	);
}
